import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';
export { z } from 'zod';

// src/common/openapi.ts
extendZodWithOpenApi(z);
z.config({
  customError: (issue) => {
    switch (issue.code) {
      case "invalid_type":
        return "This field is required";
      case "too_small":
        if (issue.origin === "string") {
          return issue.minimum === 1 ? "This field is required" : `Enter at least ${issue.minimum} characters`;
        }
        if (issue.origin === "number") return `Enter a value of at least ${issue.minimum}`;
        if (issue.origin === "array") return "Select at least one option";
        return void 0;
      case "too_big":
        if (issue.origin === "string") return `Enter at most ${issue.maximum} characters`;
        if (issue.origin === "number") return `Enter a value of at most ${issue.maximum}`;
        return void 0;
      case "invalid_format":
        if (issue.format === "uuid") return "Select an option";
        if (issue.format === "email") return "Enter a valid email address";
        if (issue.format === "url") return "Enter a valid URL";
        return "Enter a valid value";
      default:
        return void 0;
    }
  }
});

// src/common/index.ts
var uuidSchema = z.uuid("Select an option").openapi("Uuid");
var idSchema = z.coerce.number().int().positive().openapi("Id");
var slugSchema = z.string().trim().min(1, "Enter a slug").max(64).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and hyphens only").openapi("Slug");
var timestampSchema = z.iso.datetime({ offset: true }).openapi("Timestamp");
var dateOnlySchema = z.iso.date().openapi("DateOnly");
var emailSchema = z.email("Enter a valid email address").max(254).openapi("Email");
var phoneSchema = z.string().trim().regex(/^\+?[0-9]{7,15}$/, "Enter a valid phone number").openapi("Phone");
var pincodeSchema = z.string().trim().regex(/^[A-Za-z0-9]{4,10}$/, "Enter a valid pincode").openapi("Pincode");
var countryCodeSchema = z.string().trim().regex(/^[A-Z]{2}$/, "Enter a valid country code").openapi("CountryCode");
var currencyCodeSchema = z.string().trim().regex(/^[A-Z]{3}$/).openapi("CurrencyCode");
var urlSchema = z.url().max(2048).openapi("Url");
var moneySchema = z.string().regex(/^\d{1,10}\.\d{2}$/).openapi("Money");
var signedMoneySchema = z.string().regex(/^-?\d{1,10}\.\d{2}$/).openapi("SignedMoney");
var percentSchema = z.number().min(0).max(100).openapi("Percent");
var quantitySchema = z.number().int().min(0).openapi("Quantity");
var idempotencyKeySchema = z.string().trim().min(8).max(128).openapi("IdempotencyKey");
var requestIdSchema = z.string().trim().min(1).max(64).openapi("RequestId");
var ErrorCode = {
  VALIDATION_ERROR: "VALIDATION_ERROR",
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  INTERNAL_ERROR: "INTERNAL_ERROR"
};
var errorCodeSchema = z.enum(ErrorCode).openapi("ErrorCode");
var errorDetailSchema = z.object({ path: z.string(), message: z.string() }).openapi("ErrorDetail");
var errorBodySchema = z.object({
  code: errorCodeSchema,
  message: z.string(),
  details: z.array(errorDetailSchema).optional(),
  requestId: requestIdSchema
}).openapi("ErrorBody");
var errorResponseSchema = z.object({ error: errorBodySchema }).openapi("ErrorResponse");
var MAX_PAGE_SIZE = 100;
var paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0)
}).openapi("PaginationMeta");
var paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(20)
}).openapi("PaginationQuery");
var sortOrderSchema = z.enum(["asc", "desc"]).openapi("SortOrder");
var sortQuerySchema = z.object({
  sort: z.string().trim().min(1).max(64).optional(),
  order: sortOrderSchema.default("desc")
}).openapi("SortQuery");
var searchQuerySchema = z.object({ search: z.string().trim().min(1).max(160).optional() }).openapi("SearchQuery");
var listQuerySchema = paginationQuerySchema.extend(sortQuerySchema.shape).extend(searchQuerySchema.shape).openapi("ListQuery");
var dateRangeQuerySchema = z.object({ dateFrom: timestampSchema.optional(), dateTo: timestampSchema.optional() }).openapi("DateRangeQuery");
var booleanQuerySchema = z.union([z.boolean(), z.stringbool()]).openapi("BooleanQuery");
var idParamSchema = z.object({ id: uuidSchema }).openapi("IdParam");
var slugParamSchema = z.object({ slug: slugSchema }).openapi("SlugParam");
var bulkIdsSchema = z.object({ ids: z.array(uuidSchema).min(1).max(100) }).openapi("BulkIds");
var actorSchema = z.object({ id: uuidSchema, name: z.string(), role: z.string() }).openapi("Actor");
var okEnvelope = (data) => z.object({ data });
var paginatedEnvelope = (item) => z.object({ data: z.array(item), meta: paginationMetaSchema });
var successResponseSchema = z.object({ data: z.object({ success: z.literal(true) }) }).openapi("SuccessResponse");
var countResponseSchema = z.object({ data: z.object({ count: z.number().int().min(0) }) }).openapi("CountResponse");

export { ErrorCode, MAX_PAGE_SIZE, actorSchema, booleanQuerySchema, bulkIdsSchema, countResponseSchema, countryCodeSchema, currencyCodeSchema, dateOnlySchema, dateRangeQuerySchema, emailSchema, errorBodySchema, errorCodeSchema, errorDetailSchema, errorResponseSchema, idParamSchema, idSchema, idempotencyKeySchema, listQuerySchema, moneySchema, okEnvelope, paginatedEnvelope, paginationMetaSchema, paginationQuerySchema, percentSchema, phoneSchema, pincodeSchema, quantitySchema, requestIdSchema, searchQuerySchema, signedMoneySchema, slugParamSchema, slugSchema, sortOrderSchema, sortQuerySchema, successResponseSchema, timestampSchema, urlSchema, uuidSchema };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map